import { Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { MapPin, ArrowRight, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Seo } from '../components/academy/Seo';
import BrandHero from '../components/BrandHero';
import { LOCATIONS } from '../data/locations';

const LocationsIndex = () => {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Service Areas', path: '/locations' },
  ];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0e0e10 0%, #17171a 55%, #0e0e10 100%)' }}>
      <Seo
        siteName="CarLens"
        title="Service Areas in Northeast Florida"
        description="CarLens provides independent mobile pre-purchase vehicle inspections across Northeast Florida. Find your city and book your Vehicle Decision Report today."
        path="/locations"
        breadcrumbs={breadcrumbs}
      />
      <BrandHero />
      <div className="max-w-5xl mx-auto px-5 py-12 md:py-16">
        <div className="inline-flex items-center gap-2 rounded-full border border-orange-500/40 bg-orange-500/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-orange-300 mb-5">
          <MapPin className="w-3.5 h-3.5" /> Northeast Florida
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Space Grotesk' }} data-testid="locations-h1">
          CarLens Service Areas
        </h1>
        <p className="mt-5 text-lg leading-8 text-zinc-300 max-w-2xl">
          Independent, mobile pre-purchase vehicle inspections across Northeast Florida. Pick your city to see local buying insights, services, and FAQs — then book your inspection and <span className="text-white font-semibold">know the truth before you buy.</span>
        </p>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {LOCATIONS.map((c) => (
            <Link key={c.slug} to={`/locations/${c.slug}`} className="group rounded-2xl border border-zinc-800 bg-[#161618] p-6 hover:border-orange-500/50 transition-colors" data-testid={`locations-card-${c.slug}`}>
              <MapPin className="w-6 h-6 text-orange-400 mb-3" />
              <h2 className="text-lg font-bold text-white group-hover:text-orange-300 transition-colors" style={{ fontFamily: 'Space Grotesk' }}>{c.name}</h2>
              <p className="text-xs text-zinc-500 mt-0.5">{c.county}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-orange-400">View {c.name} <ArrowRight className="w-3.5 h-3.5" /></span>
            </Link>
          ))}
        </div>

        <div className="mt-12 rounded-3xl bg-gradient-to-br from-orange-500 to-orange-600 p-8 sm:p-10 text-center">
          <ShieldCheck className="w-10 h-10 text-white mx-auto mb-3" />
          <h3 className="text-2xl sm:text-3xl font-extrabold text-white" style={{ fontFamily: 'Space Grotesk' }}>Don’t see your city?</h3>
          <p className="mt-2 text-orange-50">We serve all of Northeast Florida. Book an inspection and we’ll come to the vehicle.</p>
          <Button onClick={() => navigate('/book')} className="mt-6 rounded-full bg-white text-orange-600 hover:bg-orange-50 px-8 py-5 h-auto text-base font-semibold" data-testid="locations-book-btn">
            Book Your Inspection <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LocationsIndex;
